/**
 * runidea.ai — Phase 14 Wave 2 — Impersonation banner.
 *
 * Sticky red top banner shown anywhere in the app whenever the currently
 * authenticated session was minted via an admin impersonate-call (the
 * backend stamps `impersonated_by` on /api/auth/me). Clicking "Exit"
 * calls /api/admin/stop-impersonating, refreshes the session (which now
 * resolves back to the original admin), then routes the admin back to
 * /admin/users.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [stopping, setStopping] = useState(false);

  if (!user?.impersonated_by) return null;

  const stop = async () => {
    setStopping(true);
    try {
      await api.post("/admin/stop-impersonating");
      await refresh();
      toast.success("Restored admin session");
      navigate("/admin/users");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stop impersonation");
    } finally {
      setStopping(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      role="alert"
      className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-red-600 px-4 py-2 pt-[calc(env(safe-area-inset-top)+0.5rem)] text-sm font-medium text-white shadow-md"
    >
      <span className="flex items-center gap-2">
        <Eye className="h-4 w-4 flex-shrink-0" />
        <span className="hidden sm:inline">🔴</span>
        <span>
          Impersonating{" "}
          <span className="rounded bg-white/15 px-1.5 py-0.5 font-mono text-xs">
            {user.email}
          </span>
        </span>
      </span>
      <button
        type="button"
        onClick={stop}
        disabled={stopping}
        data-testid="stop-impersonating"
        className="inline-flex items-center gap-1 rounded bg-white/15 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider transition-colors hover:bg-white/25 disabled:opacity-60"
      >
        {stopping ? <Loader2 className="h-3 w-3 animate-spin" /> : <EyeOff className="h-3 w-3" />}
        Exit
      </button>
    </div>
  );
}
